"use client";
import image from "@/constants/image";
import { APP_ROUTES } from "@/constants/routes";
import { cn } from "@/lib/utils";
import { Favourite } from "@/models/favourite";
import { Report, TYPE_FAVORITE } from "@/models/report";
import { reportShare } from "@/models/reportShare";
import { formatDate } from "@/utils/formatNumber";
import { getRedirectUrl } from "@/utils/navigation";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import { Box, IconButton, Tooltip } from "@mui/material";
import { FlameIcon } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { Montserrat } from "next/font/google";
import Image from "next/image";
import Link from "next/link";

const montserrat = Montserrat({
  subsets: ["latin"],
});

interface CardReportProps extends Report {
  isLogin: boolean;
  onlyFavorite?: boolean;
  isMobile?: boolean;
  tokenShare?: string;
  reportShare?: reportShare;
  favourite?: Omit<Favourite, "report" | "reportShare">;
  handleAddFavorite?: (id: number) => void;
}

const CardReport = ({
  slug,
  title,
  haveEmbed,
  haveInteractive,
  havePDF,
  prioritize,
  publishedDate,
  createdAt,
  isLogin = false,
  onlyFavorite,
  isMobile = false,
  categories,
  handleAddFavorite,
  tokenShare,
  reportShare,
  favourite,
  ...item
}: CardReportProps) => {
  const t = useTranslations("messages");
  const locale = useLocale();
  const mainUrl = getRedirectUrl(
    slug,
    locale,
    prioritize,
    haveEmbed,
    havePDF,
    haveInteractive
  );
  const powerbiUrl = APP_ROUTES(locale).HOME.DETAIL.MODE.POWERBI.to(slug);
  const pdfUrl = APP_ROUTES(locale).HOME.DETAIL.MODE.PDF.to(slug);
  const embedUrl = APP_ROUTES(locale).HOME.DETAIL.MODE.EMBED.to(slug);
  const shareUrl = tokenShare
    ? APP_ROUTES(locale).SHARED.to(tokenShare)
    : undefined;
  const totalPagePDF = item?.reportAttachments?.[0]?.pages || 0;
  const categoriesList =
    categories &&
    categories?.map(
      (itemCategory: any, index: any) =>
        itemCategory?.name + `${categories.length - 1 > index ? ", " : ""}`
    );
  return (
    <Box className="col-span-12 md:col-span-6 xl:col-span-4 ">
      <div className="grid gap-4">
        <Link
          href={shareUrl || mainUrl}
          className="grid gap-3 group cursor-pointer no-underline report-card-link"
        >
          <Box className="gap-3 flex items-center">
            <AccessTimeIcon
              className="bg-ciBlue_900 rounded-full text-white !w-[16px] !h-[16px]"
              height={16}
              width={16}
            />
            <p
              className="font-medium text-xs/[14.63px] text-gray_60 m-0"
              translation-key="common_published_date"
            >
              {t("common_published_date", {
                date: formatDate(String(publishedDate || createdAt), locale),
              })}
            </p>
          </Box>
          <Box className="relative w-full aspect-[397/223] bg-slate-200 rounded-lg overflow-hidden border-[1px] border-gray_20">
            <Image
              priority
              src={item?.image ?? image.imageNullValue}
              alt={title}
              fill
              sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 33vw"
              quality={90}
              className="!object-contain group-hover:brightness-90"
            />
            {isLogin && (
              <Box
                className={`absolute top-3 right-3 ${onlyFavorite || isMobile ? "flex" : "hidden group-hover:flex"}`}
              >
                <Tooltip
                  title={
                    <span
                      className={`${montserrat.className}`}
                      translation-key={
                        !item?.isFavorite
                          ? "common_favorite"
                          : "common_favorited"
                      }
                    >
                      {!item?.isFavorite
                        ? t("common_favorite")
                        : t("common_favorited")}
                    </span>
                  }
                  disableInteractive
                  placement="bottom"
                  slotProps={{
                    popper: {
                      modifiers: [
                        {
                          name: "offset",
                          options: {
                            offset: [0, -12],
                          },
                        },
                      ],
                    },
                  }}
                >
                  <IconButton
                    className="!bg-white !p-1 !rounded h-8 cursor-pointer hover:bg-white"
                    onClick={async (e) => {
                      e.preventDefault();
                      handleAddFavorite?.(item?.id);
                    }}
                  >
                    {!item?.isFavorite ? (
                      <FavoriteBorderIcon className="w-6 h-6 text-warning iconFavorite" />
                    ) : (
                      <FavoriteIcon className="w-6 h-6 text-warning iconFavorite" />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
          <Box className="flex  flex-col items-start justify-center">
            <Box
              className="grid gap-1 w-full"
              // onClick={() => handleRedirect(item?.slug, item?.havePDF, item?.haveInteractive)}
            >
              <Box className="overflow-hidden min-h-[24px]">
                <p className="truncate w-full font-normal text-sm/6 text-gray_90 m-0">
                  {categoriesList}
                </p>
              </Box>
              <p
                className={cn(
                  "!font-bold !text-base/[19.5px] !text-eerie_black group-hover:!text-ciBlue_900 m-0 line-clamp-2"
                )}
              >
                {title}
              </p>
            </Box>
          </Box>
        </Link>
        <Box className="gap-4 flex items-center mt-auto max-h-[24px]">
          {haveInteractive && !reportShare && (
            <Link
              href={powerbiUrl}
              className={cn(
                "gap-2 flex items-center  cursor-pointer no-underline report-card-interactive-link"
              )}
            >
              <Image src={image.icDouble} alt="icDouble" height={24} />
              <p
                className="font-medium text-sm/[17.07px] text-gray_80 m-0 hover:!text-ciBlue_900 hover:underline"
                translation-key="home_interactive"
              >
                {t("home_interactive")}
              </p>
            </Link>
          )}
          {havePDF && !reportShare && (
            <Link
              href={pdfUrl}
              className={cn(
                "gap-2 flex items-center cursor-pointer no-underline report-card-pdf-link"
              )}
            >
              <Image src={image.iconPDF} alt="iconPDF" height={24} />
              <p
                className="font-medium text-sm/[17.07px] text-gray_80 m-0 hover:!text-ciBlue_900 hover:underline"
                translation-key="home_pdf"
              >
                {t("home_pdf", {
                  number: totalPagePDF || 0,
                })}
              </p>
            </Link>
          )}
          {haveEmbed && !reportShare && (
            <Link
              href={embedUrl}
              className={cn(
                "gap-2 flex items-center cursor-pointer no-underline report-card-pdf-link"
              )}
            >
              <FlameIcon className="w-6 h-6 text-cimigoGreen" />
              <p
                className="font-medium text-sm/[17.07px] text-gray_80 m-0 hover:!text-ciBlue_900 hover:underline"
                translation-key="home_iframe"
              >
                Iframe
              </p>
            </Link>
          )}
          {havePDF &&
            reportShare &&
            favourite &&
            Number(favourite?.type) !== TYPE_FAVORITE.FAVORITE && (
              <Link
                href={shareUrl || ""}
                className="gap-2 flex items-center cursor-pointer no-underline report-card-pdf-share-by-user-link"
              >
                <Image src={image.iconPDF} alt="iconPDF" height={24} />
                <p
                  className="font-medium text-sm/[17.07px] text-gray_80 m-0 hover:!text-ciBlue_900 hover:underline"
                  translation-key="home_pdf_share_by_user"
                >
                  {t("home_pdf_share_by_user", {
                    number: favourite?.pages?.length || 0,
                    name:
                      reportShare?.user?.fullName ||
                      reportShare?.user?.firstName +
                        " " +
                        reportShare?.user?.lastName,
                  })}
                </p>
              </Link>
            )}
        </Box>
      </div>
    </Box>
  );
};

export default CardReport;
